import { ACTIVE_SUBSCRIPTION_STATUSES } from "./config";
import { OrganizationBillingPlan } from "./types";

import type { OrganizationSubscription } from "./types";

export const OrganizationEntitlementState = {
  GUEST_TRIAL: "guest-trial",
  PAID: "paid",
  SUSPENDED_EXPIRED: "suspended-expired",
} as const;

export type OrganizationEntitlementState =
  (typeof OrganizationEntitlementState)[keyof typeof OrganizationEntitlementState];

export interface OrganizationCapabilities {
  createSurveys: boolean;
  viewPhraseLibrary: boolean;
  viewStructureLibrary: boolean;
  useBundledDefaultTemplate: boolean;
  useAiGeneration: boolean;
  exportReports: boolean;
  uploadTemplates: boolean;
  editPhraseLibrary: boolean;
  editStructureLibrary: boolean;
}

export interface OrganizationEntitlement {
  state: OrganizationEntitlementState;
  plan: OrganizationBillingPlan | null;
  subscriptionStatus: string | null;
  capabilities: OrganizationCapabilities;
}

const BASE_CAPABILITIES: OrganizationCapabilities = {
  createSurveys: true,
  viewPhraseLibrary: true,
  viewStructureLibrary: true,
  useBundledDefaultTemplate: true,
  useAiGeneration: true,
  exportReports: false,
  uploadTemplates: false,
  editPhraseLibrary: false,
  editStructureLibrary: false,
};

const SUSPENDED_CAPABILITIES: OrganizationCapabilities = {
  ...BASE_CAPABILITIES,
  useAiGeneration: false,
};

const PAID_CAPABILITIES: OrganizationCapabilities = {
  ...BASE_CAPABILITIES,
  useAiGeneration: true,
  exportReports: true,
  uploadTemplates: true,
  editPhraseLibrary: true,
  editStructureLibrary: true,
};

export const deriveOrganizationEntitlement = (
  subscription: OrganizationSubscription | null | undefined,
): OrganizationEntitlement => {
  if (!subscription) {
    return {
      state: OrganizationEntitlementState.GUEST_TRIAL,
      plan: null,
      subscriptionStatus: null,
      capabilities: BASE_CAPABILITIES,
    };
  }

  if (subscription.plan === OrganizationBillingPlan.INTERNAL) {
    return {
      state: OrganizationEntitlementState.PAID,
      plan: subscription.plan,
      subscriptionStatus: subscription.status,
      capabilities: PAID_CAPABILITIES,
    };
  }

  const isPaid = ACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status);

  return {
    state: isPaid
      ? OrganizationEntitlementState.PAID
      : OrganizationEntitlementState.SUSPENDED_EXPIRED,
    plan: subscription.plan,
    subscriptionStatus: subscription.status,
    capabilities: isPaid ? PAID_CAPABILITIES : SUSPENDED_CAPABILITIES,
  };
};
